/**
 * «Додати об'єкт» in three steps (TZ-DEYE, D5/D6): 1) «Інвертори» — the SN
 * from the inverter's sticker, typed by hand, one or several (spaces dropped,
 * repeats removed); no DeyeCloud sign-in; an SN an active object has —
 * «вже підключено». The objects are created at once, «Очікує доступу», with
 * no capacity, power or region; nothing is called in DeyeCloud. 2) (D3)
 * «Доступ для Phage.Energy»: how to open the station to our business account
 * in DeyeCloud, «Перевірити доступ» per object — the inverter found by its SN:
 * its settings are saved, its history downloads (D4) and its parameters come
 * from DeyeCloud (D6) — «Зробити пізніше» or «Далі». 3) (D6) «Параметри» —
 * only after «Доступ відкрито»: filled from DeyeCloud («з DeyeCloud»), the
 * client may correct them, chooses the tariff and what was not found (the DSO
 * of a region with several, the CT). No outage reserve here (20 % by default,
 * changed in «Налаштування»).
 */
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { InputCt, SiteResponse, TariffType } from 'shared';

import { AppShell } from '../../components/AppShell.js';
import {
  CtField,
  RegionFields,
  TariffFields,
  defaultRateTexts,
  parseRates,
} from '../../components/ObjectFields.js';
import { PhageAccessHowTo, accessStatusKey, useAccessCheck } from '../../components/PhageAccess.js';
import { useToast } from '../../components/Toast.js';
import { apiCode, errorText } from '../../components/errors.js';
import { modelLabel, parseNum, serialOf } from '../../components/format.js';
import { type RatedTariff, type ZoneName, hasRates, zoneCells } from '../../components/tariff.js';
import { useConnectSites, usePhageAccessInfo } from '../../hooks/use-sites.js';
import { useLocale } from '../../i18n/locale-context.js';
import { api } from '../../lib/api-client.js';

interface Draft {
  name: string;
  cap: string;
  pmax: string;
  allowed: string;
  ct: InputCt;
  region: string;
  osr: string;
}

/** D6: a parameter as DeyeCloud filled it — the field's text. */
const numText = (v: number | null) => (v === null ? '' : String(v));

/** D6: «з DeyeCloud» / «вручну» next to a field. */
function Source({ site, field }: { site: SiteResponse; field: string }) {
  const { t } = useLocale();
  const src = site.params_source[field];
  return src ? <small className="sub">{t(src === 'deye' ? 'add.src.deye' : 'add.src.manual')}</small> : null;
}

/** The SN as typed from the sticker: spaces dropped. */
const cleanSn = (v: string) => v.replace(/\s+/g, '');

/** The SNs entered: cleaned, empty ones and repeats removed, in order. */
const snsOf = (rows: readonly string[]) => [...new Set(rows.map(cleanSn).filter(Boolean))];

/** Step 3: a connected object, its access status and «Перевірити доступ». */
function ShareRow({ site, onSite }: { site: SiteResponse; onSite: (site: SiteResponse) => void }) {
  const { t } = useLocale();
  const c = useAccessCheck(site, onSite);
  const status = site.phage_access.status;
  return (
    <div className="share-row">
      <span>
        <b>{site.name}</b>
        <span className="sub num" style={{ display: 'block' }}>
          {[modelLabel(site.model), `SN ${serialOf(site.vendor_site_id)}`].filter(Boolean).join(' · ')}
        </span>
      </span>
      <span className={`chip${status === 'closed' ? ' warn' : ''}`}>{t(accessStatusKey(status))}</span>
      <button type="button" className="btn small" disabled={c.disabled} onClick={c.check}>
        {t('access.check')}
      </button>
      {c.message ? (
        <p className={c.bad ? 'err' : 'sub'} aria-live="polite">
          {c.message}
        </p>
      ) : null}
    </div>
  );
}

export function AddSitePage() {
  const { t, tf, tp, locale } = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const connect = useConnectSites();
  const accessInfo = usePhageAccessInfo();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [rows, setRows] = useState<string[]>(['']);
  const [checked1, setChecked1] = useState(false);
  /** SNs the server answered «вже підключено» for. */
  const [taken, setTaken] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [tariff, setTariff] = useState<TariffType>('dam');
  const [rates, setRates] = useState<Partial<Record<ZoneName, string>>>({});
  const [checked3, setChecked3] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState<SiteResponse[]>([]);

  const chosen = snsOf(rows);
  /** D6: «Параметри» only for objects whose access is open. */
  const open = connected.filter((s) => s.phage_access.status === 'open');

  // D6: the objects are created right after the SNs — waiting for access, no parameters.
  const onConnect = async (e: FormEvent) => {
    e.preventDefault();
    setChecked1(true);
    if (chosen.length === 0 || chosen.some((sn) => taken.includes(sn))) return;
    setRows(chosen);
    setConnectError(null);
    setBusy(true);
    try {
      const sites = await connect.mutateAsync({
        sites: chosen.map((sn) => ({ device_sn: sn, name: sn })),
      });
      setConnected(sites);
      setStep(2);
      window.scrollTo(0, 0);
      toast.show(tp('add.done', sites.length));
    } catch (err) {
      const code = apiCode(err);
      const sns = (err as { details?: { device_sns?: string[] } }).details?.device_sns ?? [];
      if (code === 'conflict' && sns.length > 0) {
        setTaken(sns);
        setConnectError(tf('add.err.taken', { sn: sns.join(', ') }));
      } else setConnectError(errorText(err, tf));
    } finally {
      setBusy(false);
    }
  };

  const toParams = () => {
    const next: Record<string, Draft> = {};
    for (const s of open) {
      const region = s.region ?? '';
      next[s.id] = drafts[s.id] ?? {
        name: s.name,
        cap: numText(s.capacity_kwh),
        pmax: numText(s.p_max_kw),
        allowed: numText(s.allowed_power_kw),
        ct: s.input_ct,
        region,
        osr: s.osr_code ?? '',
      };
    }
    setDrafts(next);
    setConnectError(null);
    setStep(3);
    window.scrollTo(0, 0);
  };

  const zone = hasRates(tariff) ? (tariff as RatedTariff) : null;
  const parsedRates = zone ? parseRates(zone, rates) : null;
  const draftProblems = (d: Draft) => ({
    name: !d.name.trim(),
    cap: !(parseNum(d.cap) > 0),
    pmax: !(parseNum(d.pmax) > 0),
    // Optional: empty — no limit but the inverter's.
    allowed: !!d.allowed.trim() && !(parseNum(d.allowed) > 0),
    region: !d.region,
  });
  const valid =
    open.every((s) => {
      const d = drafts[s.id];
      return d && !Object.values(draftProblems(d)).some(Boolean);
    }) &&
    (!zone || !!parsedRates);

  const onSave = async (e: FormEvent) => {
    e.preventDefault();
    setChecked3(true);
    if (!valid) return;
    setConnectError(null);
    setBusy(true);
    try {
      for (const s of open) {
        const d = drafts[s.id] as Draft;
        // Only what differs from DeyeCloud's value becomes «вручну».
        const saved = await api.patch<SiteResponse>(`/api/sites/${encodeURIComponent(s.id)}/settings`, {
          tariff_type: tariff,
          ...(d.name.trim() !== s.name ? { name: d.name.trim() } : {}),
          ...(parseNum(d.cap) !== s.capacity_kwh ? { capacity_kwh: parseNum(d.cap) } : {}),
          ...(parseNum(d.pmax) !== s.p_max_kw ? { p_max_kw: parseNum(d.pmax) } : {}),
          ...(d.allowed.trim() && parseNum(d.allowed) !== s.allowed_power_kw
            ? { allowed_power_kw: parseNum(d.allowed) }
            : {}),
          ...(d.ct !== s.input_ct ? { input_ct: d.ct } : {}),
          ...(d.region !== s.region ? { region: d.region } : {}),
          ...(d.osr !== (s.osr_code ?? '') ? { osr_code: d.osr || null } : {}),
        });
        // The access check left the object in the cache without tariff and
        // parameters: the object screen must open with what was just saved.
        qc.setQueryData(['site', s.id], saved);
      }
      let ratesSaved = true;
      if (zone && parsedRates) {
        const cells = zoneCells(zone, parsedRates);
        const results = await Promise.allSettled(
          open.map((s) =>
            api.put(`/api/sites/${encodeURIComponent(s.id)}/tariff`, { hourly_local_per_kwh: cells }),
          ),
        );
        ratesSaved = results.every((r) => r.status === 'fulfilled');
      }
      if (!ratesSaved) toast.show(t('add.err.rates'));
      void qc.invalidateQueries({ queryKey: ['sites'] });
      for (const s of open) {
        for (const key of ['site', 'site-overview', 'tariff', 'site-money', 'site-potential']) {
          void qc.invalidateQueries({ queryKey: [key, s.id] });
        }
      }
      leave('access.toast.done');
    } catch (err) {
      setConnectError(errorText(err, tf));
    } finally {
      setBusy(false);
    }
  };

  const leave = (toastKey: 'access.toast.done' | 'access.toast.later') => {
    const first = connected[0];
    navigate(connected.length === 1 && first ? `/app/sites/${first.id}` : '/app/sites', { replace: true });
    toast.show(t(toastKey));
  };

  const steps = [
    { n: 1, key: 'add.step.inverters' as const },
    { n: 2, key: 'add.step.access' as const },
    { n: 3, key: 'add.step.params' as const },
  ];

  return (
    <AppShell>
      <div className="view">
        <div className="crumbs">
          <Link to="/app/sites">{t('nav.objects')}</Link>
          <span aria-hidden="true">/</span>
          <span>{t('add.crumb')}</span>
        </div>
        <div className="page-head">
          <h1>{t('add.title')}</h1>
        </div>
        <ol className="steps" aria-label={t('add.steps')}>
          {steps.map((s) => (
            <li
              key={s.n}
              className={s.n === step ? 'on' : s.n < step ? 'done' : undefined}
              aria-current={s.n === step ? 'step' : undefined}
            >
              {t(s.key)}
            </li>
          ))}
        </ol>

        {step === 1 ? (
          <section className="card">
            <h2>{t('add.s1.title')}</h2>
            <p className="sub" style={{ margin: '6px 0 16px', maxWidth: '70ch' }}>
              {t('add.s1.sub')}
            </p>
            <form className="form-grid" style={{ maxWidth: 520 }} noValidate onSubmit={onConnect}>
              {rows.map((row, i) => {
                const sn = cleanSn(row);
                const repeat = !!sn && rows.slice(0, i).some((r) => cleanSn(r) === sn);
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: rows are edited in place
                  <label className="field full" key={i}>
                    <span>{t('add.sn')}</span>
                    <span style={{ display: 'flex', gap: 8 }}>
                      <input
                        className="num"
                        autoComplete="off"
                        spellCheck={false}
                        value={row}
                        style={{ flex: 1 }}
                        onChange={(e) => {
                          setRows(rows.map((r, k) => (k === i ? e.target.value : r)));
                          setConnectError(null);
                        }}
                        aria-invalid={taken.includes(sn) || (checked1 && chosen.length === 0)}
                      />
                      {rows.length > 1 ? (
                        <button
                          type="button"
                          className="btn small"
                          onClick={() => setRows(rows.filter((_, k) => k !== i))}
                        >
                          {t('add.sn_remove')}
                        </button>
                      ) : null}
                    </span>
                    {taken.includes(sn) && !repeat ? (
                      <small className="err">{t('add.connected')}</small>
                    ) : null}
                  </label>
                );
              })}
              {checked1 && chosen.length === 0 ? <p className="err full">{t('add.sn_empty')}</p> : null}
              {connectError ? (
                <p className="err full" role="alert">
                  {connectError}
                </p>
              ) : null}
              <div className="actions full" style={{ marginTop: 4 }}>
                <button type="button" className="btn" onClick={() => setRows([...rows, ''])}>
                  {t('add.sn_more')}
                </button>
                <button type="submit" className="btn primary" disabled={busy}>
                  {tp('add.connect', Math.max(chosen.length, 1))}
                </button>
              </div>
            </form>
          </section>
        ) : null}

        {step === 2 ? (
          <section className="card">
            <h2>{t('access.title')}</h2>
            <p className="sub" style={{ margin: '6px 0 16px', maxWidth: '72ch' }}>
              {t('access.sub')}
            </p>
            <div className="group">
              <h3 className="group-title">{t('access.howto.title')}</h3>
              <PhageAccessHowTo name={accessInfo.data?.company_name ?? null} />
            </div>
            <div className="group">
              <h3 className="group-title">{t('access.check.title')}</h3>
              <div className="pick-list">
                {connected.map((s) => (
                  <ShareRow
                    key={s.id}
                    site={s}
                    onSite={(next) => setConnected((all) => all.map((x) => (x.id === next.id ? next : x)))}
                  />
                ))}
              </div>
            </div>
            <div className="actions">
              <button type="button" className="btn" onClick={() => leave('access.toast.later')}>
                {t('access.later')}
              </button>
              <button type="button" className="btn primary" disabled={open.length === 0} onClick={toParams}>
                {t('common.next')}
              </button>
            </div>
          </section>
        ) : null}

        {step === 3 ? (
          <section className="card">
            <h2>{t('add.s3.title')}</h2>
            <p className="sub" style={{ margin: '6px 0 18px', maxWidth: '72ch' }}>
              {t('add.s3.sub')}
            </p>
            <form noValidate onSubmit={onSave}>
              {open.map((s, i) => {
                const d = drafts[s.id] as Draft;
                const bad = checked3
                  ? draftProblems(d)
                  : { name: false, cap: false, pmax: false, allowed: false, region: false };
                const patch = (p: Partial<Draft>) => setDrafts({ ...drafts, [s.id]: { ...d, ...p } });
                return (
                  <div className="group" key={s.id}>
                    <h3 className="group-title num">SN {serialOf(s.vendor_site_id)}</h3>
                    <div className="form-grid">
                      <label className="field full">
                        <span>{t('add.app_name')}</span>
                        <input
                          value={d.name}
                          onChange={(e) => patch({ name: e.target.value })}
                          aria-invalid={bad.name}
                        />
                        {bad.name ? (
                          <small className="err">{t('site.st.err.name')}</small>
                        ) : (
                          <Source site={s} field="name" />
                        )}
                      </label>
                      <label className="field">
                        <span>{t('site.st.cap')}</span>
                        <input
                          inputMode="decimal"
                          value={d.cap}
                          onChange={(e) => patch({ cap: e.target.value })}
                          aria-invalid={bad.cap}
                        />
                        {bad.cap ? (
                          <small className="err">{t('site.st.err.cap')}</small>
                        ) : (
                          <Source site={s} field="capacity_kwh" />
                        )}
                      </label>
                      <label className="field">
                        <span>{t('site.st.pmax')}</span>
                        <input
                          inputMode="decimal"
                          value={d.pmax}
                          onChange={(e) => patch({ pmax: e.target.value })}
                          aria-invalid={bad.pmax}
                        />
                        {bad.pmax ? (
                          <small className="err">{t('site.st.err.pmax')}</small>
                        ) : (
                          <Source site={s} field="p_max_kw" />
                        )}
                      </label>
                      <label className="field">
                        <span>{t('site.st.allowed')}</span>
                        <input
                          inputMode="decimal"
                          value={d.allowed}
                          onChange={(e) => patch({ allowed: e.target.value })}
                          aria-invalid={bad.allowed}
                        />
                        {bad.allowed ? (
                          <small className="err">{t('site.st.err.allowed')}</small>
                        ) : (
                          <small>{t('site.st.allowed_hint')}</small>
                        )}
                      </label>
                      <div className="field full">
                        <span>{t('add.pv')}</span>
                        <span className="num">
                          {s.has_pv
                            ? s.pv_kw_peak
                              ? tf('site.chip.pv_kw', { kw: String(s.pv_kw_peak) })
                              : t('site.chip.pv_yes')
                            : t('site.chip.pv_no')}
                        </span>
                        <Source site={s} field="pv_kw_peak" />
                      </div>
                      <RegionFields
                        region={d.region}
                        osr={d.osr}
                        onChange={(region, osr) => patch({ region, osr })}
                      />
                      {s.params_source.region ? (
                        <Source site={s} field="region" />
                      ) : bad.region ? (
                        <small className="err">{t('add.err.region')}</small>
                      ) : null}
                      {s.params_source.input_ct === 'deye' ? null : (
                        <CtField name={`ct-${i}`} value={d.ct} onChange={(ct) => patch({ ct })} />
                      )}
                    </div>
                  </div>
                );
              })}
              <TariffFields
                name="a-tariff"
                tariff={tariff}
                rates={rates}
                unread={false}
                onChange={(next, r) => {
                  setTariff(next);
                  const z = hasRates(next) ? (next as RatedTariff) : null;
                  setRates(z && next !== tariff ? defaultRateTexts(z, locale) : r);
                }}
              />
              {checked3 && zone && !parsedRates ? <p className="err">{t('site.st.err.rate')}</p> : null}
              {connectError ? (
                <p className="err" role="alert" style={{ marginTop: 14 }}>
                  {connectError}
                </p>
              ) : null}
              <div className="actions">
                <button type="button" className="btn" onClick={() => leave('access.toast.done')}>
                  {t('access.later')}
                </button>
                <button type="submit" className="btn primary" disabled={busy}>
                  {t('access.done')}
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </div>
    </AppShell>
  );
}
